"use client";

import { useEffect, useId, useRef, useState } from "react";

import cn from "classnames";

import { Button } from "@/components/button/button.component";
import { ArrowRightIcon } from "@/components/icons/arrowRight.icon";
import { SaleIcon } from "@/components/icons/sale.icon";
import { Modal } from "@/components/modal/modalContainer.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { PERSONALIZATION_MODAL_SEEN_KEY } from "@/lib/personalization/constants";

import { CONTENT_MODAL_QUERY_PARAM } from "@/constants/contentModal.constants";
import type { PersonalizationQueryResult } from "@/sanity.types";

import { PersonalizationConfetti } from "./personalizationConfetti.component";

function hasSeenPersonalizationModal(): boolean {
  try {
    return sessionStorage.getItem(PERSONALIZATION_MODAL_SEEN_KEY) === "true";
  } catch {
    return false;
  }
}

function markPersonalizationModalSeen(): void {
  try {
    sessionStorage.setItem(PERSONALIZATION_MODAL_SEEN_KEY, "true");
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

export function PersonalizationModal({
  content,
  showModal = false,
}: PersonalizationModalProps) {
  const [dialog, setDialog] = useState<HTMLDialogElement | null>(null);
  const titleId = useId();
  const dismissedIntentionallyRef = useRef(false);

  const markIntentionalDismiss = () => {
    dismissedIntentionallyRef.current = true;
  };

  const close = () => {
    if (dismissedIntentionallyRef.current) {
      markPersonalizationModalSeen();
      dismissedIntentionallyRef.current = false;
    }
  };

  useEffect(() => {
    if (!dialog || !showModal || dialog.open) return;
    if (hasSeenPersonalizationModal()) return;
    dialog.showModal();
  }, [dialog, showModal]);

  return (
    <Modal
      ref={setDialog}
      onClose={close}
      showHeader={false}
      aria-labelledby={titleId}
      className={cn(
        "relative max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[587px]! gap-0! overflow-hidden rounded-3xl! py-0! text-white shadow-none",
        // Figma: linear-gradient(104deg, primary-400 → secondary-100 → secondary → secondary-100 → primary-400)
        "bg-[linear-gradient(104deg,rgb(0_184_241/0.95)_-7.18%,rgb(7_64_139/0.95)_24.69%,rgb(0_34_106/0.95)_55.42%,rgb(7_64_139/0.95)_79.9%,rgb(0_184_241/0.95)_111.2%)]!",
      )}>
      {/* -mx-2 offsets Modal's default px-6 so side inset matches design (16px) */}
      <div className="relative -mx-2 flex flex-col items-center pt-6 pb-8 text-center">
        <form method="dialog" className="absolute top-2 right-2 z-20">
          <Button
            type="submit"
            aria-label="Close modal"
            onClick={markIntentionalDismiss}
            className="flex h-8 w-8 cursor-pointer items-center justify-center text-white outline-none focus-visible:outline-none">
            <ArrowRightIcon
              width={20}
              height={20}
              aria-hidden
              className="rotate-45"
            />
          </Button>
        </form>

        <PersonalizationConfetti className="pointer-events-none absolute top-6 left-1/2 h-auto w-[min(100%,502px)] -translate-x-1/2" />

        <div className="relative z-10 flex w-full max-w-[420px] flex-col items-center gap-6 pt-20">
          {content.tagText && (
            <Text
              variant="preHeaderSm"
              className="flex items-center gap-2 uppercase">
              <SaleIcon
                width={16}
                height={16}
                aria-hidden
                className="shrink-0"
              />
              <RichText>{content.tagText}</RichText>
            </Text>
          )}
          {content.callOutText && (
            <Text variant="h5" id={titleId} className="text-white">
              <RichText>{content.callOutText}</RichText>
            </Text>
          )}
          {content.discountText && (
            <Text variant="h2" className="text-primary">
              <RichText>{content.discountText}</RichText>
            </Text>
          )}
          {content.discountDetails && (
            <Text variant="body4" className="text-white">
              <RichText>{content.discountDetails}</RichText>
            </Text>
          )}
          <form
            method="dialog"
            className="mt-2 flex w-full max-w-[320px] justify-center">
            <Button type="submit" primary onClick={markIntentionalDismiss}>
              {content.buttonText ?? "Continue Shopping"}
            </Button>
          </form>

          {content.legalCallout && (
            <Text variant="legal" className="text-white/90">
              <RichText interactiveQueryParam={CONTENT_MODAL_QUERY_PARAM}>
                {content.legalCallout}
              </RichText>
            </Text>
          )}
        </div>
      </div>
    </Modal>
  );
}

type PersonalizationModalProps = {
  content: NonNullable<PersonalizationQueryResult>;
  showModal?: boolean;
};
